import { LINK_QUALITY_START, takeSample, type LinkQuality } from "./hud/link-quality.ts";
import type { HudFeed } from "./hud/store.ts";

/**
 * Where the watch reads the browser's network state and hears it change.
 */
export interface LinkWatchHost {
  readonly hud: Pick<HudFeed, "setLink">;
  readonly events: Pick<Window, "addEventListener" | "removeEventListener">;
  readonly online: () => boolean;
}

/**
 * The watch's host on a page: the window's events and the navigator's online flag, and on a page without them (a test
 * run with no DOM) no events and an online link.
 *
 * @param hud The HUD.
 * @returns The host.
 */
export function pageLinkHost(hud: LinkWatchHost["hud"]): LinkWatchHost {
  const scope = typeof window === "undefined" ? null : window;
  const noop = (): void => undefined;
  return {
    hud,
    events: scope ?? { addEventListener: noop, removeEventListener: noop },
    online: () => typeof navigator === "undefined" || navigator.onLine !== false,
  };
}

/**
 * The page's link as the connection mark shows it (P1.9-T46): Offline while the browser says it is offline, Slow while
 * the keepalive round trips run past the player's own normal (`takeSample`, `LINK_QUALITY`), else nothing. It tells the
 * HUD's `setLink`, under which a background reconnect's own mark takes precedence, and it says nothing once the
 * session has ended, so the notice stands alone. It starts with the first round trip of a connection and keeps its
 * baseline through background reconnects.
 */
export class LinkWatch {
  private quality: LinkQuality = LINK_QUALITY_START;
  private offline = false;
  private active = false;
  private readonly changed = (): void => {
    this.offline = !this.host.online();
    this.show();
  };

  /**
   * @param host The HUD, the browser's events and its online flag.
   */
  public constructor(private readonly host: LinkWatchHost) {}

  /**
   * Takes one keepalive round trip. The first one starts the watch.
   *
   * @param rttMs The round trip in milliseconds.
   */
  public sample(rttMs: number): void {
    this.begin();
    this.quality = takeSample(this.quality, rttMs);
    this.show();
  }

  /**
   * Ends the watch: the listeners go, the baseline is forgotten and the mark clears. The next sample starts it again.
   */
  public stop(): void {
    if (this.active) {
      this.host.events.removeEventListener("offline", this.changed);
      this.host.events.removeEventListener("online", this.changed);
    }
    this.active = false;
    this.quality = LINK_QUALITY_START;
    this.offline = false;
    this.host.hud.setLink(null);
  }

  /**
   * Starts listening to the browser's offline and online events, reading its state now.
   */
  private begin(): void {
    if (this.active) {
      return;
    }
    this.active = true;
    this.offline = !this.host.online();
    this.host.events.addEventListener("offline", this.changed);
    this.host.events.addEventListener("online", this.changed);
  }

  /**
   * Shows what the link is: offline first, then slow, else clear.
   */
  private show(): void {
    this.host.hud.setLink(this.offline ? { state: "offline" } : this.quality.slow ? { state: "slow" } : null);
  }
}
